import TeachersPetHost from './teachersPet/TeachersPetHost.jsx';
// src/App.jsx
// REPLACED in Sprint 2b-1 — React Router v6 skeleton per spec section 3.6.
// Replaces both Sprint 2a's verification shell AND prototype's currentPage state machine.
//
// Route map:
//   /                            → redirect to /home
//   /home                        → HomePage (real)
//   /login                       → LoginPage (real)
//   /dashboard, /account, /cart, /checkout, /booking, /set-text,
//   /pack-placeholder, /skills-workshop, /literature-study,
//   /terminology, /admin, /pack-payment-choice, /pack-payment-payme
//                                → PlaceholderPage stub (13 routes)
//   *                            → redirect to /home
//
// Auth guard: protected routes wrapped in <AuthGuard> (src/auth/) — unauth → /login with state.from preserved (P10).
// Admin guard: /admin wrapped in <AdminGuard> — verifies Firebase custom claim `admin === true`.
// Theme by route: themeByPath map drives Navbar theme prop.
// Hide Navbar: 4 routes (set-text, admin, skills-workshop, literature-study) skip Navbar.

import React, { useEffect } from 'react';
import {
    BrowserRouter, Routes, Route, Navigate, Outlet, useLocation
} from 'react-router-dom';

import Navbar from './components/Navbar.jsx';
import { HelpFeedbackProvider } from './components/HelpFeedback.jsx';
import AnalyticsBridge from './analytics/AnalyticsBridge.jsx';
import useAppState from './storage/useAppState.js';
import { useSiteContentBridge, AuthenticatedSiteContentBridge } from './storage/useSiteContentBridge.js';
import { usePacksBridge } from './storage/usePacksBridge.js';
import SchoolPage from './pages/SchoolPage.jsx';
import AccountPage from './pages/AccountPage.jsx';
import BookingPage from './pages/BookingPage.jsx';
import CartPage from './pages/CartPage.jsx';
import CheckoutPage from './pages/CheckoutPage.jsx';
import PackOfferPage from './pages/PackOfferPage.jsx';
import CheckoutResultPage from './pages/CheckoutResultPage.jsx';
import DashboardPage from './pages/DashboardPage.jsx';
import TerminologyPage from './pages/TerminologyPage.jsx';
import HomePage from './pages/HomePage.jsx';
import LegalPage from './pages/LegalPage.jsx';
import FaqPage from './pages/FaqPage.jsx';
import LoginPage from './pages/LoginPage.jsx';
import PackPreviewPage from './pages/PackPreviewPage.jsx';
import PersonalStatementPage from './pages/PersonalStatementPage.jsx';
import PersonalStatementRequestPage from './pages/PersonalStatementRequestPage.jsx';
import PackPurchasePayMePage from './pages/PackPurchasePayMePage.jsx';
import PaymentMethodChoicePage from './pages/PaymentMethodChoicePage.jsx';
import StudyPartnerPage from './pages/StudyPartnerPage.jsx';
import PackReaderSetText from './pages/PackReaderSetText.jsx';
import PackReaderLiterature from './pages/PackReaderLiterature.jsx';
import PackReaderSkills from './pages/PackReaderSkills.jsx';
import PackReaderPoetry from './pages/PackReaderPoetry.jsx';
import { AuthProvider } from './auth/AuthProvider.jsx';
import { AuthGuard } from './auth/AuthGuard.jsx';
// The existing AdminGuard admits the route before this import is requested.
const AdminPage = React.lazy(() => import('./pages/AdminPage.jsx'));
// DEV-only visual harness (library-2-shelf). LAZY + behind the flag on purpose: with a
// static import the module stays in the build graph, and although Rollup does drop the
// dead route from the shipped JS, the original source still lands in the sourcemap
// (vite.config.js sets sourcemap: true). As a ternary over a dynamic import,
// `import.meta.env.DEV` folds to false in a production build, the import() becomes
// unreachable and the module leaves the graph entirely -- the built output has no trace.
const DevLibraryPage = import.meta.env.DEV
    ? React.lazy(() => import('./pages/DevLibraryPage.jsx'))
    : null;
const DevLessonPage = import.meta.env.DEV
    ? React.lazy(() => import('./pages/DevLessonPage.jsx'))
    : null;
const DevHomePage = import.meta.env.DEV
    ? React.lazy(() => import('./pages/DevHomePage.jsx'))
    : null;
import { AdminGuard } from './auth/AdminGuard.jsx';
import { useReadingPrefs, ReadingPrefsContext } from './pages/useReadingPrefs.js';

// === Configuration constants ===

// Theme by route path (replaces prototype's themeByPage)
const themeByPath = {
    '/home': 'gold',
    '/login': 'gold',
    '/legal': 'gold',
    '/faq': 'gold',
    '/personal-statement': 'gold',
    '/dashboard': 'burgundy',
    '/account': 'midnight',
    '/booking': 'leather',
    '/cart': 'charcoal',
    '/checkout': 'charcoal',
    '/checkout/success': 'charcoal',
    '/checkout/cancel': 'charcoal',
    '/set-text': 'gold',
    '/pack-placeholder': 'gold',
    '/skills-workshop': 'burgundy',
    '/literature-study': 'midnight',
    '/poetry-anthology': 'gold',
    '/terminology': 'gold',
    '/admin': 'gold',
    '/pack-payment-choice': 'burgundy',
    '/pack-payment-payme': 'burgundy',
    '/study-partner': 'gold'
};

// Routes that hide Navbar (have their own internal navigation)
const ROUTES_HIDING_NAVBAR = new Set([
    '/set-text', '/admin', '/skills-workshop', '/literature-study', '/poetry-anthology'
]);

// === Layout (per-route side effects + Navbar + Outlet) ===
// Phase 2 Sprint 1: AuthGuard / AdminGuard moved to per-route element wrapping
// (AMENDMENT 3); Layout reduced to Navbar + Outlet only.

const Layout = ({ state }) => {
    const location = useLocation();
    const path = location.pathname;
    const theme = themeByPath[path] || 'gold';
    const hideNavbar = ROUTES_HIDING_NAVBAR.has(path);

    useEffect(() => {
        try { localStorage.setItem('btl_v2_page', path.replace(/^\//, '')); } catch {}
        window.scrollTo(0, 0);
    }, [path]);

    return (
        <>
            <HelpFeedbackProvider contactLinks={state.siteText?.contactLinks}>
            {!hideNavbar && <Navbar state={state} theme={theme} />}
            <Outlet />
            </HelpFeedbackProvider>
            <TeachersPetHost />
            <AnalyticsBridge state={state} />
        </>
    );
};

// === PlaceholderPage for unimplemented routes ===

const PlaceholderPage = () => {
    const location = useLocation();
    const slug = location.pathname.replace(/^\//, '');
    const title = slug.replace(/-/g, ' ');
    return (
        <div className="max-w-2xl mx-auto px-6 py-16">
            <div className="border border-book-accent/30 bg-book-page/40 shadow-book p-10">
                <h1 className="font-display text-book-accent text-3xl mb-4">
                    {title}
                </h1>
                <p className="font-serif italic text-book-muted text-lg mb-4">
                    This page migrates in a later sprint.
                </p>
                <p className="font-body text-book-text">
                    Current path: <code>{location.pathname}</code>
                </p>
            </div>
        </div>
    );
};

// === Root App ===

export default function App() {
    const state = useAppState();
    // WP-5 H5: load admin-published copy/config from Firestore into app state
    // (public read; inert in mock -> defaults stand).
    useSiteContentBridge(state);
    // Self-Serve Packs Slice 1: load the live /packs catalog into state.packs (public
    // read; inert in mock / empty / unreadable -> the storefront stays empty, no demo packs).
    usePacksBridge(state);

    // Single source of truth for the color theme (night/day/muji), lifted here so it
    // drives BOTH the reader (via ReadingPrefsContext, consumed by ReaderPageShell +
    // portals) AND non-reader pages + the Navbar (via a class on <html>). A change made
    // in the reader prefs bar updates this one instance, so the whole app reacts.
    const prefs = useReadingPrefs();
    useEffect(() => {
        const el = document.documentElement;
        el.classList.remove('theme-night', 'theme-day', 'theme-muji', 'reading-light', 'reading-muji', 'pack-themed');
        if (prefs.colorMode === 'muji') {
            // theme-muji drives the navbar + non-book namespace overrides; reading-muji
            // pack-themed makes the book-* Minimalist rules (slice 3) apply site-wide too.
            el.classList.add('theme-muji', 'reading-muji', 'pack-themed');
        } else if (prefs.colorMode === 'day') {
            // Light is FULL site-wide (mirrors muji): reading-light pack-themed applies the
            // book-* day skin to non-reader book pages; theme-day drives the navbar + the
            // non-book namespace Light overrides (Dashboard/Account/Cart/Checkout/Booking).
            el.classList.add('theme-day', 'reading-light', 'pack-themed');
        } else {
            el.classList.add('theme-night'); // DEFAULT: no rules target it -> byte-for-byte unchanged
        }
        // (Phase 1a: the html.theme-a preview flag is retired -- Direction A IS the skin now.)
    }, [prefs.colorMode]);

    return (
        <ReadingPrefsContext.Provider value={prefs}>
        <AuthProvider useAppState={state}>
            <AuthenticatedSiteContentBridge state={state} />
            <BrowserRouter>
                <Routes>
                    {/* DEV-ONLY visual harness (library-2-shelf). The library page is
                        behind AuthGuard, so the shelves + Continue card are unreachable
                        in the local preview; this renders them from mock packs. The
                        branch is inside `import.meta.env.DEV`, which Vite replaces with
                        `false` when building for production, so dead-code elimination
                        drops the route and the lazy chunk together -- the built output
                        contains no trace of the harness. */}
                    {import.meta.env.DEV && DevLibraryPage && (
                        <Route path="/__dev/library" element={
                            <React.Suspense fallback={null}><DevLibraryPage /></React.Suspense>
                        } />
                    )}
                    {import.meta.env.DEV && DevLessonPage && (
                        <Route path="/__dev/lesson" element={
                            <React.Suspense fallback={null}><DevLessonPage /></React.Suspense>
                        } />
                    )}
                    {import.meta.env.DEV && DevHomePage && (
                        <Route path="/__dev/home" element={
                            <React.Suspense fallback={null}><DevHomePage /></React.Suspense>
                        } />
                    )}

                    {/* Root redirect */}
                    <Route path="/" element={<Navigate to="/home" replace />} />

                    {/* All routes share the Layout (Navbar + Outlet) */}
                    <Route element={<Layout state={state} />}>
                        {/* Public routes (unwrapped per spec §0.6 success criterion 5 + MA-6) */}
                        <Route path="/home" element={<HomePage state={state} />} />
                        <Route path="/login" element={<LoginPage state={state} />} />
                        <Route path="/dashboard" element={<DashboardPage state={state} />} />
                        <Route path="/legal" element={<LegalPage />} />
                        {/* PUBLIC FAQ (home-packfinder): the same items as the home page's
                            FAQ section, prerendered to dist/faq with FAQPage JSON-LD. */}
                        <Route path="/faq" element={<FaqPage state={state} />} />
                        {/* PUBLIC pack free-preview (pack-free-preview slice): deliberately
                            NOT behind AuthGuard -- the getPackPreview CF serves free items
                            only, so a signed-out prospect can read the free sample. */}
                        <Route path="/preview/:packId" element={<PackPreviewPage />} />
                        {/* PUBLIC Personal Statement landing (personal-statement slice):
                            a marketing/funnel page, so prospects AND crawlers reach it
                            unauthenticated. Its REQUEST flow below is auth-gated. */}
                        <Route path="/personal-statement" element={<PersonalStatementPage state={state} />} />

                        {/* Protected routes (AuthGuard per AMENDMENT 3) */}
                        <Route path="/personal-statement/request" element={<AuthGuard><PersonalStatementRequestPage /></AuthGuard>} />
                        <Route path="/school" element={<AuthGuard><SchoolPage state={state} /></AuthGuard>} />
                        <Route path="/teachers-pet" element={<AuthGuard><div /></AuthGuard>} />
                        <Route path="/account" element={<AuthGuard><AccountPage state={state} /></AuthGuard>} />
                        <Route path="/cart" element={<AuthGuard><CartPage state={state} /></AuthGuard>} />
                        <Route path="/checkout" element={<AuthGuard><CheckoutPage state={state} /></AuthGuard>} />
                        <Route path="/pack-offer/:packId" element={<AuthGuard><PackOfferPage state={state} /></AuthGuard>} />
                        <Route path="/checkout/success" element={<AuthGuard><CheckoutResultPage outcome="success" /></AuthGuard>} />
                        <Route path="/checkout/cancel" element={<AuthGuard><CheckoutResultPage outcome="cancel" /></AuthGuard>} />

                        <Route path="/booking" element={<AuthGuard><BookingPage state={state} /></AuthGuard>} />
                        <Route path="/study-partner" element={<AuthGuard><StudyPartnerPage state={state} /></AuthGuard>} />
                        <Route path="/study-partner/:packId" element={<AuthGuard><StudyPartnerPage state={state} /></AuthGuard>} />
                        {/* Set-text student reader (Slice 1: free-only). Generic by :packId.
                            Bare /set-text (dashboard nav) redirects to the launch set-text pack
                            for now -- the container itself is NOT hardcoded to any pack. */}
                        <Route path="/set-text" element={<Navigate to="/set-text/edexcel-set-text" replace />} />
                        <Route path="/set-text/:packId" element={<AuthGuard><PackReaderSetText /></AuthGuard>} />
                        <Route path="/pack-placeholder" element={<AuthGuard><PlaceholderPage /></AuthGuard>} />
                        {/* Bare /skills-workshop (dashboard nav) has no single pack -> placeholder.
                            Skills-workshop student reader is generic by :packId (mirrors literature). */}
                        <Route path="/skills-workshop" element={<AuthGuard><PlaceholderPage /></AuthGuard>} />
                        <Route path="/skills-workshop/:packId" element={<AuthGuard><PackReaderSkills /></AuthGuard>} />
                        {/* Literature-study student reader. Generic by :packId. Bare
                            /literature-study has no single pack, so it redirects to the
                            storefront (NO hardcoded pack id). */}
                        <Route path="/literature-study" element={<Navigate to="/dashboard" replace />} />
                        <Route path="/literature-study/:packId" element={<AuthGuard><PackReaderLiterature /></AuthGuard>} />
                        {/* Poetry-anthology student reader (PA1: placeholder reader shell; poem
                            sections + editor land in later slices). Generic by :packId. Bare
                            /poetry-anthology has no single pack -> redirect to the storefront
                            (mirrors /literature-study; NO hardcoded pack id). */}
                        <Route path="/poetry-anthology" element={<Navigate to="/dashboard" replace />} />
                        <Route path="/poetry-anthology/:packId" element={<AuthGuard><PackReaderPoetry /></AuthGuard>} />
                        <Route path="/terminology" element={<AuthGuard><TerminologyPage state={state} /></AuthGuard>} />

                        {/* Admin route (AdminGuard — verifies admin custom claim; renders access-denied otherwise) */}
                        <Route path="/admin" element={
                            <AdminGuard>
                                <React.Suspense fallback={
                                    <div role="status" aria-live="polite"
                                        className="min-h-[calc(100vh-4rem)] bg-book-bg paper-texture flex items-center justify-center px-4 font-display text-book-muted">
                                        Loading admin console…
                                    </div>
                                }>
                                    <AdminPage state={state} />
                                </React.Suspense>
                            </AdminGuard>
                        } />

                        {/* Payment subflow routes (AuthGuard) */}
                        <Route path="/pack-payment-choice/:packId" element={<AuthGuard><PaymentMethodChoicePage state={state} /></AuthGuard>} />
                        <Route path="/pack-payment-choice" element={<AuthGuard><PaymentMethodChoicePage state={state} /></AuthGuard>} />
                        <Route path="/pack-payment-payme/:packId" element={<AuthGuard><PackPurchasePayMePage state={state} /></AuthGuard>} />
                        <Route path="/pack-payment-payme" element={<AuthGuard><PackPurchasePayMePage state={state} /></AuthGuard>} />
                    </Route>

                    {/* Catch-all → /home */}
                    <Route path="*" element={<Navigate to="/home" replace />} />
                </Routes>
            </BrowserRouter>
        </AuthProvider>
        </ReadingPrefsContext.Provider>
    );
}
